import {
  CatalogOverride,
  DayOfWeek,
  MenuVisibility,
  OrderType,
  POSSettings,
  POSType,
  StoreOrderingForm,
  StoreOrderingRequest,
  StoreOrderingResponse,
} from 'api/ordering-service/ordering-service.types';
import {
  additionalPropertiesToObject,
  ExtendedAttributeControlType,
  extendedAttributesToArray,
  posLocationGuidMapping,
} from 'api/ordering-service/ordering-service.utils';
import { Schedule } from 'components/forms/fields/scheduler-field/scheduler-field.context';
import omit from 'lodash.omit';

export const transformMenuImportConfigForResponse = (
  response: StoreOrderingResponse,
  pos: POSSettings,
): StoreOrderingForm['menuImportConfig'] => {
  return {
    ...response.menuImportConfig,
    additionalExtendedAttributes: extendedAttributesToArray(
      response.menuImportConfig?.extendedAttributes,
      [...getExcludedMenuProperties(pos.type), 'CATALOG_ID'],
    ),
    posEntryProperties: getPosPropertiesToArray(
      response.menuImportConfig?.extendedAttributes,
      getExcludedMenuProperties(pos.type),
      pos.type,
    ).filter(p => typeof p.value !== 'boolean'),

    posSwitchProperties: getPosPropertiesToArray(
      response.menuImportConfig?.extendedAttributes,
      getExcludedMenuProperties(pos.type),
      pos.type,
    ).filter(p => typeof p.value === 'boolean'),
    catalogIds:
      response?.menuImportConfig?.extendedAttributes?.['CATALOG_ID']?.split(
        ',',
      ) ?? [],
  };
};

export const transformMenuImportConfigForRequest = (
  data: StoreOrderingForm,
): StoreOrderingRequest['menuImportConfig'] => {
  // combine menuImportConfig array extended attributes with pos-specific extended attributes
  const menuImportExtendedAttributes = {
    ...additionalPropertiesToObject(
      data.menuImportConfig.additionalExtendedAttributes,
    ),
    ...additionalPropertiesToObject(data.menuImportConfig.posEntryProperties),
    ...additionalPropertiesToObject(data.menuImportConfig.posSwitchProperties),
  };

  // set the store id separately in extended attributes if it's a pos that requires it
  if (posLocationGuidMapping[data.pos.type]?.menu) {
    menuImportExtendedAttributes[posLocationGuidMapping[data.pos.type].menu] =
      data.pos.storeId as any;
  }

  if (data.menuImportConfig.catalogIds?.length > 0) {
    menuImportExtendedAttributes['CATALOG_ID'] =
      data.menuImportConfig.catalogIds.join(',');
  }

  const connectorName = posConnectorNameMapping[data.pos.type];

  return {
    ...omit(
      data.menuImportConfig,
      'additionalExtendedAttributes',
      'posEntryProperties',
      'posSwitchProperties',
      'catalogIds',
    ),
    extendedAttributes: menuImportExtendedAttributes,
    connectorGatewayName: connectorName !== '' ? data.pos.type : '',
    connectorName: connectorName ?? '',
    importType: data.menuImportConfig.importType ?? 'INDEPENDANT_MENU_IMPORT',
    defaultLanguage: data.menuImportConfig.defaultLanguage ?? 'EN',
  };
};

export const transformMenuVisibilityForResponse = (
  response: StoreOrderingResponse,
): MenuVisibility =>
  transform2(response.catalogOverrides, transform2(response.groupOverrides));

const transform2 = (
  overrides: CatalogOverride[],
  menuVisibility: MenuVisibility = {},
): MenuVisibility => {
  return (
    overrides?.reduce((acc: MenuVisibility, override) => {
      const catalogId = override.catalogId;
      const catalogLevel = acc[catalogId] ?? {};
      const groupId = override.groupId ?? 'all';
      const groupLevel = catalogLevel[groupId] ?? [];
      const applicableOrderTypes = override.applicableOrderTypes;

      return {
        ...acc,
        [catalogId]: {
          ...catalogLevel,
          [groupId]: Object.entries(override.override).reduce(
            (acc, [day, times]) => {
              return [
                ...acc,
                ...times.map(time => ({
                  additionalData: applicableOrderTypes,
                  day: day as DayOfWeek,
                  startTime: time.startTime,
                  endTime: time.endTime,
                })),
              ];
            },
            groupLevel,
          ),
        },
      };
    }, menuVisibility) ?? menuVisibility
  );
};

export const transformCatalogOverridesForRequest = (
  menuVisibility: StoreOrderingForm['menuVisibility'],
): StoreOrderingRequest['catalogOverrides'] => {
  return Object.entries(menuVisibility).reduce((acc, [catalogId, groups]) => {
    Object.entries(groups).forEach(([groupId, visibility]) => {
      if (groupId === 'all')
        acc = acc.concat(overridesByOrderType(catalogId, groupId, visibility));
    });
    return acc;
  }, []);
};

export const transformGroupOverridesForRequest = (
  menuVisibility: StoreOrderingForm['menuVisibility'],
): StoreOrderingRequest['groupOverrides'] => {
  return Object.entries(menuVisibility).reduce((acc, [catalogId, groups]) => {
    Object.entries(groups).forEach(([groupId, visibility]) => {
      if (groupId !== 'all')
        acc = acc.concat(overridesByOrderType(catalogId, groupId, visibility));
    });
    return acc;
  }, []);
};

const overridesByOrderType = (
  catalogId: string,
  groupId: string,
  schedule: Schedule[],
): CatalogOverride[] => {
  const pickupAndDelivery: CatalogOverride = overridesByOrderTypeHelper(
    catalogId,
    groupId,
    ['PICKUP', 'DELIVERY'],
    schedule,
  );
  const pickup: CatalogOverride = overridesByOrderTypeHelper(
    catalogId,
    groupId,
    ['PICKUP'],
    schedule,
  );
  const delivery: CatalogOverride = overridesByOrderTypeHelper(
    catalogId,
    groupId,
    ['DELIVERY'],
    schedule,
  );

  return [pickupAndDelivery, pickup, delivery].filter(Boolean);
};

const overridesByOrderTypeHelper = (
  catalogId: string,
  groupId: string,
  orderType: OrderType[],
  schedule: Schedule[],
): CatalogOverride => {
  const filteredVisibility: Schedule[] = schedule.filter(
    (visibility: Schedule<OrderType[]>) =>
      visibility.additionalData?.every(type => orderType.includes(type)) &&
      visibility.additionalData?.length === orderType.length,
  );

  if (filteredVisibility.length > 0)
    return {
      catalogId,
      groupId: groupId === 'all' ? null : groupId,
      applicableOrderTypes: orderType,
      override: filteredVisibility.reduce((acc, visibility) => {
        if (!acc[visibility.day]) {
          acc[visibility.day] = [];
        }
        acc[visibility.day].push({
          startTime: visibility.startTime,
          endTime: visibility.endTime,
        });
        return acc;
      }, {}),
    };
};

export const getExcludedMenuProperties = (posType: POSType): string[] => [
  ...Object.keys(specialMenuProperties[posType] ?? {}),
  ...(posLocationGuidMapping[posType]?.menu
    ? [posLocationGuidMapping[posType].menu]
    : []),
];

export const getPosPropertiesToArray = (
  ex: Record<string, any>,
  excludeKeys: string[],
  posType,
) => {
  const posProperties = [];
  excludeKeys.forEach(key => {
    if (!posLocationGuidMapping[posType]?.menu?.includes(key)) {
      if (ex[key] !== undefined) {
        let newValue = ex.key;
        if (ex[key]?.toLowerCase() === 'true') {
          newValue = true;
        } else if (ex[key]?.toLowerCase() === 'false') {
          newValue = false;
        } else {
          newValue = ex[key];
        }
        posProperties.push({ key: key, value: newValue });
      } else {
        let newValue =
          specialMenuProperties[posType][key] === 'text' ? '' : false;
        posProperties.push({ key: key, value: newValue });
      }
    }
  });
  return posProperties;
};

const commonMenuProperties: Record<string, ExtendedAttributeControlType> = {
  NUTRITIONAL_INFO_LINK: 'text',
};

export const specialMenuProperties: {
  [K in POSType]: Record<string, ExtendedAttributeControlType>;
} = {
  '': { ...commonMenuProperties },
  'incentivio-order': { ...commonMenuProperties },
  'incentivio-connector-ncc': { ...commonMenuProperties },
  'incentivio-conpos-brink': {
    // EXT_STORE_GUID: 'text',
    LOCATION_TOKEN: 'text',
    API_BASE_URL: 'text',
    ...commonMenuProperties,
  },
  'incentivio-conpos-lightspeed': {
    // EXT_STORE_GUID: 'text',
    COMPANY_ID: 'text',
    LIGHTSPEED_BASE_URL: 'text',
    LIGHTSPEED_COMPANY_ID: 'text',
    LIGHTSPEED_DEVICE_ID: 'text',
    LIGHTSPEED_PASSWORD: 'text',
    LIGHTSPEED_USER_NAME: 'text',
    ...commonMenuProperties,
  },
  'incentivio-conpos-qu': {
    DELIVERY_ORDER_TYPE_ID: 'text',
    EXT_COMPANY_ID: 'text',
    EXT_MEAL_PERIOD: 'text',
    EXT_ORDER_CHANNEL: 'text',
    EXT_ORDER_TYPE: 'text',
    EXT_PASSWORD: 'text',
    // EXT_STORE_GUID: 'text',
    EXT_USER_NAME: 'text',
    PICKUP_ORDER_TYPE_ID: 'text',
    OVERRIDE_DISPLAY_RANK: 'switch',
    OVERRIDE_DISPLAYINFO_TITLE: 'switch',
    RUN_AUTO_IMPORTS: 'switch',
    USE_86ING: 'switch',
    DISPLAY_CALORIES: 'switch',
    DISPLAY_ONLY_ITEM_CALORIES: 'switch',
    ...commonMenuProperties,
  },
  'incentivio-conpos-revel': {
    'API-AUTHENTICATION': 'text',
    CATALOG_EXT_ID: 'text',
    ESTABLISHMENT: 'text',
    IMPORT_MODE: 'text',
    RESTAURANT_NAME: 'text',
    ...commonMenuProperties,
  },
  'incentivio-conpos-silverware': {
    STORE_ALIAS: 'text',
    // EXT_STORE_GUID: 'text',
    OVERRIDE_DISPLAYINFO_DESCRIPTIONS: 'switch',
    OVERRIDE_DISPLAYINFO_TITLE: 'switch',
    RETAIN_STATUS: 'switch',
    RETAIN_TAX_SETTINGS: 'switch',
    ...commonMenuProperties,
  },
  'incentivio-conpos-otter': {
    // EXT_STORE_GUID: 'text',
    USE_86ING: 'switch',
    ...commonMenuProperties,
  },
  'incentivio-conpos-square': {
    // SQUARE_LOCATION_ID: 'text',
    USE_86ING: 'switch',
    RUN_AUTO_IMPORTS: 'switch',
    OVERRIDE_DISPLAY_RANK: 'switch',
    OVERRIDE_DISPLAYINFO_TITLE: 'switch',
    ...commonMenuProperties,
  },
  'incentivio-connector-spoton-pos': {
    // EXT_STORE_GUID: 'text',
    DELIVERY_ORDER_TYPE_ID: 'text',
    PICKUP_ORDER_TYPE_ID: 'text',
    RUN_AUTO_IMPORTS: 'switch',
    USE_86ING: 'switch',
    ...commonMenuProperties,
  },
  'incentivio-connector-toast': {
    // EXT_STORE_GUID: 'text',
    VISIBILITY: 'text',
    OVERRIDE_DISPLAYINFO_TITLE: 'switch',
    OVERRIDE_DISPLAYINFO_DESCRIPTIONS: 'switch',
    RUN_AUTO_IMPORTS: 'switch',
    USE_86ING: 'switch',
    OVERRIDE_DISPLAY_RANK: 'switch',
    LEGACY_IMPORT: 'switch',
    DISPLAY_CALORIES: 'switch',
    DISPLAY_ONLY_ITEM_CALORIES: 'switch',
    ...commonMenuProperties,
  },
  'incentivio-connector-simphony-pos': {},
  'incentivio-conpos-clover': {
    // EXT_STORE_GUID: 'text',
    ...commonMenuProperties,
  },
};

export const posConnectorNameMapping: {
  [K in POSType]?: string;
} = {
  'incentivio-order': '',
  'incentivio-connector-ncc': '',
  'incentivio-conpos-brink': 'BRINK CONNECTOR',
  'incentivio-conpos-lightspeed': 'LIGHTSPEED CONNECTOR',
  'incentivio-conpos-qu': 'QU CONNECTOR',
  'incentivio-conpos-revel': 'REVEL CONNECTOR',
  'incentivio-conpos-silverware': 'SILVERWARE CONNECTOR',
  'incentivio-conpos-otter': 'OTTER CONNECTOR',
  'incentivio-conpos-square': 'SQUARE CONNECTOR',
  'incentivio-connector-spoton-pos': 'SPOTON CONNECTOR',
  'incentivio-connector-toast': 'TOAST CONNECTOR',
  'incentivio-connector-simphony-pos': 'SIMPHONY',
  'incentivio-conpos-clover': 'CLOVER CONNECTOR',
};
